/**
 * Colour-picker maths — RGB ⇄ HSV, plus the hue bar's position ⇄ hue mapping.
 *
 * Ported from the mobile app's `src/features/profile/model/colorPicker.ts`, unchanged in substance.
 * The picker works in HSV because that is what the handoff draws — a saturation/value plane under a
 * hue bar — while `contrast.ts` works in HSL, which is the right space for "make this readable".
 * The two live apart for that reason.
 *
 * **The bar's stops are not an even 60° sweep**, and that is the trap this file exists to keep
 * closed: reading the hue as `position × 360` puts the thumb a different colour from the strip
 * underneath it. The angles are measured off the painted stops and interpolated between them.
 */

import { clamp01, cylinderToRgb, parseHex, toHex } from '~/shared/lib/hex';

export interface Hsv {
  /** Hue in degrees, 0–360. */
  readonly h: number;
  /** Saturation, 0–1 — the plane's x. */
  readonly s: number;
  /** Value, 0–1 — the plane's y, inverted (top = 1). */
  readonly v: number;
}

export function hsvToHex({ h, s, v }: Hsv): string {
  const c = v * s;
  return toHex(cylinderToRgb(h, c, v - c));
}

export function hexToHsv(color: string): Hsv {
  const { r, g, b } = parseHex(color);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  const v = max / 255;
  const s = max === 0 ? 0 : d / max;
  if (d === 0) return { h: 0, s, v };
  const h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return { h: h * 60, s, v };
}

/**
 * The hue bar's painted stops, verbatim from the handoff. A stylised sweep rather than an even one —
 * see the header for why that matters.
 */
export const HUE_BAR_STOPS = [
  '#ff0000',
  '#ffdd00',
  '#33dd55',
  '#00c8ff',
  '#6a5bff',
  '#ff00c8',
  '#ff0000',
] as const;

/** The stops' measured hue angles, ascending (the last wraps to 360 rather than back to 0). */
const HUE_BAR_ANGLES: readonly number[] = HUE_BAR_STOPS.map((stop, index) =>
  index === HUE_BAR_STOPS.length - 1 ? 360 : hexToHsv(stop).h,
);

const SEGMENTS = HUE_BAR_STOPS.length - 1;

/** One angle of the table, loudly — `noUncheckedIndexedAccess` is on and a silent `0` is a wrong hue. */
function angleAt(index: number): number {
  const angle = HUE_BAR_ANGLES[index];
  if (angle === undefined) throw new Error(`the hue bar has no stop #${String(index)}`);
  return angle;
}

/** Position along the bar (0–1) → hue in degrees. Clamped rather than wrapped. */
export function hueAtPosition(position: number): number {
  const t = clamp01(position) * SEGMENTS;
  const index = Math.min(Math.floor(t), SEGMENTS - 1);
  const from = angleAt(index);
  const to = angleAt(index + 1);
  return from + (to - from) * (t - index);
}

/** Hue in degrees → position along the bar (0–1). The inverse of `hueAtPosition`. */
export function positionForHue(hue: number): number {
  const h = ((hue % 360) + 360) % 360;
  for (let index = 0; index < SEGMENTS; index += 1) {
    const from = angleAt(index);
    const to = angleAt(index + 1);
    if (h <= to) return (index + (h - from) / (to - from)) / SEGMENTS;
  }
  return 1;
}

/**
 * Replace a draft's colour from a hex, keeping the hue the bar is standing on when the new colour
 * has none.
 *
 * Black, white and every grey have no hue in RGB, so round-tripping through a hex on every drag
 * would silently reset the bar to red the moment somebody dragged to the bottom of the plane. The
 * mobile sheet keeps HSV as the source of truth for the same reason; this is that rule, as a
 * function a test can reach.
 */
export function hsvFromHex(hex: string, current: Hsv): Hsv {
  const next = hexToHsv(hex);
  return next.s === 0 ? { ...next, h: current.h } : next;
}
